/**
 * PaymentTimeline — Horizontal stepper showing voucher lifecycle:
 *   Created → Approved → Paid
 *
 * Each step shows a colored icon + connector. Tooltips surface timestamps and audit info.
 * Handles the "Cancelled/Rejected" branch with a red state on step 2.
 */
import {
	alpha,
	Box,
	Stack,
	Step,
	StepConnector,
	stepConnectorClasses,
	StepLabel,
	Stepper,
	styled,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import {
	Cancel as IconCancel,
	Check as IconCheck,
	ConfirmationNumber as IconVoucher,
	Payment as IconPayment,
	ThumbUp as IconApprove,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { User, Voucher } from '../../../@types';
import { COLORS } from '../../../data';
import { UserStamp } from '../UserStamp/UserStamp';

// Types

export interface PaymentTimelineProps {
	voucher: Voucher;
	/** When true, renders without the outer card border/bg — for embedding inside another card */
	embedded?: boolean;
	/** Resolved user who created the voucher */
	creator?: Partial<User> | null;
	/** Resolved user who approved the voucher */
	approver?: Partial<User> | null;
	/** Resolved user who rejected the voucher */
	rejecter?: Partial<User> | null;
	/** Resolved user who finalized/redeemed the payment */
	redeemer?: Partial<User> | null;
	/**
	 * Optional timestamp overrides. The voucher table no longer carries
	 * approved_at / rejected_at / redeemed_at columns (story 104) — those
	 * timestamps live on `public.activity` rows now. Pass them in here so
	 * the timeline can show "when" alongside "who".
	 */
	approvedAt?: string | null;
	rejectedAt?: string | null;
	redeemedAt?: string | null;
}

type StepState = 'completed' | 'active' | 'upcoming' | 'rejected';

interface TimelineStep {
	label: string;
	description: string;
	timestamp?: string;
	user?: Partial<User> | null;
	state: StepState;
	icon: React.ElementType;
	tooltipExtra?: string;
}

// Styled connector

const TimelineConnector = styled(StepConnector)(({ theme }) => ({
	[`&.${stepConnectorClasses.alternativeLabel}`]: {
		top: 20,
	},
	[`& .${stepConnectorClasses.line}`]: {
		height: 2,
		border: 0,
		borderRadius: 1,
		backgroundColor: theme.palette.grey[200],
	},
	[`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
		backgroundColor: alpha(COLORS.info.main, 0.5),
	},
	[`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
		backgroundColor: alpha(COLORS.success.main, 0.5),
	},
}));

// Styled step icon

const StepIconContainer = styled('div')<{
	ownerState: { state: StepState };
}>(({ ownerState }) => {
	const bg =
		ownerState.state === 'completed'
			? COLORS.success.main
			: ownerState.state === 'active'
				? COLORS.info.main
				: ownerState.state === 'rejected'
					? COLORS.error.main
					: '#e0e0e0';

	const shadow =
		ownerState.state === 'active'
			? `0 0 0 3px ${alpha(COLORS.info.main, 0.15)}`
			: ownerState.state === 'rejected'
				? `0 0 0 3px ${alpha(COLORS.error.main, 0.12)}`
				: 'none';

	return {
		alignItems: 'center',
		backgroundColor: bg,
		borderRadius: '50%',
		boxShadow: shadow,
		color: '#fff',
		display: 'flex',
		height: 42,
		justifyContent: 'center',
		width: 42,
		zIndex: 1,
		transition: 'all 0.2s ease',
	};
});

// Helpers

const formatTimestamp = (iso?: string) => {
	if (!iso) return null;
	const d = new Date(iso);
	return d.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
};

const formatTimestampFull = (iso?: string) => {
	if (!iso) return null;
	const d = new Date(iso);
	return d.toLocaleString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

/**
 * Determine which step index is currently "active" based on voucher status:
 *   GENERATED / VERIFIED → step 1 (Approved is next)
 *   READY               → step 2 (Paid is next)
 *   REDEEMED            → step 3 (all done)
 *   CANCELLED           → step 1 rejected branch
 */
const getActiveStepIndex = (status: string): number => {
	switch (status) {
		case 'REDEEMED':
			return 3; // all complete
		case 'READY':
			return 2; // awaiting payment
		case 'CANCELLED':
			return 1; // rejected at approval
		default:
			return 1; // GENERATED, VERIFIED — awaiting approval
	}
};

// Component

export const PaymentTimeline = ({
	voucher,
	embedded = false,
	creator,
	approver,
	rejecter,
	redeemer,
	approvedAt,
	rejectedAt,
	redeemedAt,
}: PaymentTimelineProps) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const isCancelled = voucher.status === 'CANCELLED';
	const activeIdx = getActiveStepIndex(voucher.status);

	// Status-derived completion (the voucher table no longer carries
	// approved_at / rejected_at / redeemed_at columns — audit lives in
	// public.activity). Status is the source of truth for which lifecycle
	// steps have been reached.
	const isApproved = voucher.status === 'READY' || voucher.status === 'REDEEMED';
	const isPaid = voucher.status === 'REDEEMED';

	// Prefer activity-row timestamps (if the parent fetched them) over the
	// legacy voucher columns, which are now always null.
	const resolvedApprovedAt = approvedAt ?? voucher.approvedAt ?? undefined;
	const resolvedRejectedAt = rejectedAt ?? voucher.rejectedAt ?? undefined;
	const resolvedRedeemedAt = redeemedAt ?? voucher.redeemedAt ?? undefined;

	// Build steps
	const steps: TimelineStep[] = [
		{
			label: t('spendPlan.voucher.timeline.created', 'Created'),
			description: t('spendPlan.voucher.timeline.createdDesc', 'Payment voucher generated'),
			timestamp: voucher.created,
			user: creator,
			state: 'completed', // always complete — voucher exists
			icon: IconVoucher,
		},
		{
			label: isCancelled
				? t('spendPlan.voucher.timeline.rejected', 'Rejected')
				: t('spendPlan.voucher.timeline.approved', 'Approved'),
			description: isCancelled
				? t('spendPlan.voucher.timeline.rejectedDesc', 'Payment was rejected')
				: t('spendPlan.voucher.timeline.approvedDesc', 'Conditions verified & payment approved'),
			timestamp: (isCancelled ? resolvedRejectedAt : resolvedApprovedAt) || undefined,
			user: isCancelled ? rejecter : approver,
			state: isCancelled
				? 'rejected'
				: isApproved
					? 'completed'
					: activeIdx === 1
						? 'active'
						: 'upcoming',
			icon: isCancelled ? IconCancel : IconApprove,
			tooltipExtra: isCancelled ? voucher.rejectionReason || undefined : undefined,
		},
		{
			label: t('spendPlan.voucher.timeline.paid', 'Paid'),
			description: t('spendPlan.voucher.timeline.paidDesc', 'Payment disbursed & tokens deducted'),
			timestamp: resolvedRedeemedAt || undefined,
			user: redeemer,
			state: isPaid
				? 'completed'
				: isCancelled
					? 'upcoming'
					: activeIdx === 2
						? 'active'
						: 'upcoming',
			icon: IconPayment,
		},
	];

	// The MUI Stepper "activeStep" controls connector coloring.
	// We set it to the number of completed steps.
	const completedCount = steps.filter((s) => s.state === 'completed').length;

	// Custom step icon renderer
	const renderStepIcon = (step: TimelineStep) => {
		const StepIcon = step.state === 'completed' ? IconCheck : step.icon;
		return (
			<StepIconContainer ownerState={{ state: step.state }}>
				<StepIcon sx={{ fontSize: 22 }} />
			</StepIconContainer>
		);
	};

	// Tooltip content
	const renderTooltip = (step: TimelineStep) => (
		<Box sx={{ p: 1.25, maxWidth: 280 }}>
			<Typography
				sx={{ fontWeight: 600, fontSize: '1.375rem', color: 'common.white', lineHeight: 1.3 }}>
				{step.label}
			</Typography>
			<Typography
				sx={{
					fontSize: '1.25rem',
					color: 'grey.300',
					display: 'block',
					mt: 0.25,
					lineHeight: 1.4,
				}}>
				{step.description}
			</Typography>
			{step.timestamp && (
				<Typography
					sx={{
						fontSize: '1.25rem',
						display: 'block',
						mt: 0.5,
						fontWeight: 600,
						color: 'grey.100',
					}}>
					{formatTimestampFull(step.timestamp)}
				</Typography>
			)}
			{step.user && (
				<Typography sx={{ fontSize: '1.25rem', color: 'grey.300', display: 'block' }}>
					By: {step.user.firstName} {step.user.lastName}
				</Typography>
			)}
			{step.tooltipExtra && (
				<Typography
					sx={{
						fontSize: '1.25rem',
						display: 'block',
						mt: 0.5,
						color: 'error.light',
						fontStyle: 'italic',
					}}>
					Reason: {step.tooltipExtra}
				</Typography>
			)}
			{step.state === 'upcoming' && (
				<Typography
					sx={{
						fontSize: '1.25rem',
						color: 'grey.400',
						display: 'block',
						mt: 0.25,
						fontStyle: 'italic',
					}}>
					{t('spendPlan.voucher.timeline.notStarted', 'Not Started')}
				</Typography>
			)}
		</Box>
	);

	return (
		<Box
			sx={{
				...(!embedded && {
					bgcolor: 'white',
					border: '1px solid',
					borderColor: 'grey.200',
					borderRadius: 2,
					mb: 2,
				}),
				px: { xs: 1, sm: embedded ? 0 : 2 },
				py: embedded ? 0 : 2,
			}}>
			<Stepper
				activeStep={completedCount}
				alternativeLabel={!isMobile}
				connector={<TimelineConnector />}
				orientation={isMobile ? 'vertical' : 'horizontal'}>
				{steps.map((step, idx) => (
					<Step
						key={step.label}
						completed={step.state === 'completed'}
						active={step.state === 'active' || step.state === 'rejected'}>
						<Tooltip
							title={renderTooltip(step)}
							arrow
							placement={isMobile ? 'right' : 'top'}
							componentsProps={{
								tooltip: {
									sx: {
										maxWidth: 'none',
									},
								},
							}}>
							<StepLabel
								StepIconComponent={() => renderStepIcon(step)}
								sx={{
									cursor: 'default',
									'& .MuiStepLabel-label': {
										fontWeight: step.state === 'active' ? 600 : 500,
										fontSize: '1.25rem',
										color:
											step.state === 'completed'
												? theme.palette.grey[600]
												: step.state === 'rejected'
													? COLORS.error.main
													: step.state === 'active'
														? theme.palette.grey[700]
														: theme.palette.text.disabled,
										mt: 0.75,
									},
								}}>
								<Stack alignItems="center" spacing={0}>
									<span>{step.label}</span>
									{step.timestamp && step.user && (
										<UserStamp
											user={step.user}
											timestamp={step.timestamp}
											color={
												step.state === 'rejected'
													? COLORS.error.main
													: step.state === 'completed'
														? COLORS.success.main
														: undefined
											}
											size={22}
											fontSize="1.125rem"
											sx={{ mt: 0.25 }}
										/>
									)}
									{step.timestamp && !step.user && (
										<Typography
											variant="caption"
											sx={{
												color:
													step.state === 'rejected'
														? COLORS.error.main
														: step.state === 'completed'
															? COLORS.success.main
															: 'text.secondary',
												fontWeight: 500,
												fontSize: '1.25rem',
												lineHeight: 1.3,
												mt: 0.25,
											}}>
											{formatTimestamp(step.timestamp)}
										</Typography>
									)}
									{step.state === 'active' && !step.timestamp && (
										<Typography
											variant="caption"
											sx={{
												color: COLORS.info.main,
												fontWeight: 500,
												fontSize: '1.25rem',
												lineHeight: 1.3,
												mt: 0.25,
											}}>
											{t('spendPlan.voucher.timeline.inProgress', 'In Progress')}
										</Typography>
									)}
								</Stack>
							</StepLabel>
						</Tooltip>
					</Step>
				))}
			</Stepper>
		</Box>
	);
};
